import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Colors from "../../styles/Colors";
import { H4 } from "../../styles/Fonts";
import { getTruncNum, openNewTab } from "../../utils/common";
import { ALL_PAIRS, PairProps } from "../../utils/constant/pairs";
import PairTokenIcon from "../../components/PairTokenIcon";
import useUniswapPoolState from "../../hooks/useUniswapPoolState";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const ActivePosition: React.FC<{ pos: { [key: string]: any } }> = ({ pos }) => {
  const { OpenTime: openTime } = pos;
  const {
    BorrowRatio: borrowRatio,
    WantTokenAmount: wantTokenAmount,
    BorrowTokenAmount: borrowTokenAmount,
    WantTokenFee: wantTokenFee,
    BorrowTokenFee: borrowTokenFee,
  } = pos.PositionInfo;
  const { ContractAddr: contractAddress } = pos.DiamondFactoryV3;

  const [pair, setPair] = useState<PairProps>();

  const currentTimestamp = useMemo(
    () => Math.floor(new Date().getTime() / 1000),
    []
  );

  const { token1Price } = useUniswapPoolState(
    pair?.uniPoolAddress || "",
    pair?.borrowToken || {
      tokenSymbol: "",
      tokenFullName: "",
      tokenDecimal: 0,
      tokenAddress: "",
      tokenIcon: "",
    },
    pair?.wantToken || {
      tokenSymbol: "",
      tokenFullName: "",
      tokenDecimal: 0,
      tokenAddress: "",
      tokenIcon: "",
    },
    pair?.deployedChainId || 1
  );

  const feeApy = useMemo(
    () =>
      pair?.wantToken.tokenDecimal && pair?.borrowToken.tokenDecimal
        ? getTruncNum(
            (+wantTokenFee / 10 ** pair?.wantToken.tokenDecimal +
              ((+borrowTokenFee / 10 ** pair?.borrowToken.tokenDecimal) *
                token1Price) /
                (+wantTokenAmount / 10 ** pair?.wantToken.tokenDecimal)) *
              ((60 * 60 * 24) / (+currentTimestamp - +openTime)) *
              365 *
              100,
            2
          )
        : 0,
    [
      wantTokenFee,
      borrowTokenFee,
      token1Price,
      wantTokenAmount,
      currentTimestamp,
      openTime,
      pair?.wantToken.tokenDecimal,
      pair?.borrowToken.tokenDecimal,
    ]
  );

  const pairs = Object.values(ALL_PAIRS).flat();

  const getFactoryPair = useCallback(() => {
    const pair = pairs.find(
      (item) =>
        item.factoryAddress.toUpperCase() === contractAddress.toUpperCase()
    );
    if (pair) {
      setPair(pair);
    }
  }, [contractAddress, pairs]);

  useEffect(() => {
    getFactoryPair();
  }, [getFactoryPair]);

  const openUniPage = (_positionId: string) => {
    openNewTab(
      `https://app.uniswap.org/#/pool/${_positionId}?chain=${pos.DiamondFactoryV3.Chain}`
    );
  };

  return (
    <PositionRow key={pos.positionId}>
      <PositionDetail>
        <PositionHeaderM>Pool</PositionHeaderM>
        <Row>
          {pair?.borrowToken.tokenIcon && pair?.wantToken.tokenIcon && (
            <PairTokenIcon
              borrowTokenIcon={pair?.borrowToken.tokenIcon}
              wantTokenIcon={pair?.wantToken.tokenIcon}
            />
          )}
          <PositionValue>{pair?.label}</PositionValue>
        </Row>
      </PositionDetail>
      <PositionDetail>
        <PositionHeaderM>Position Value</PositionHeaderM>
        <ValuesContainer>
          <PositionValueSmall>
            {pair?.wantToken
              ? `${getTruncNum(
                  wantTokenAmount / 10 ** pair?.wantToken.tokenDecimal,
                  6
                )} ${pair?.wantToken.tokenSymbol}`
              : "-"}
          </PositionValueSmall>
          <PositionValueSmall>
            {pair?.borrowToken
              ? `${getTruncNum(
                  borrowTokenAmount / 10 ** pair?.borrowToken.tokenDecimal,
                  6
                )} ${pair?.borrowToken.tokenSymbol}`
              : "-"}
          </PositionValueSmall>
        </ValuesContainer>
      </PositionDetail>
      <PositionDetail>
        <PositionHeaderM>Earned Fee</PositionHeaderM>
        <ValuesContainer>
          <PositionValueSmall>
            {pair?.wantToken
              ? `${getTruncNum(
                  wantTokenFee / 10 ** pair?.wantToken.tokenDecimal,
                  6
                )} ${pair?.wantToken.tokenSymbol}`
              : "-"}
          </PositionValueSmall>
          <PositionValueSmall>
            {pair?.borrowToken
              ? `${getTruncNum(
                  borrowTokenFee / 10 ** pair?.borrowToken.tokenDecimal,
                  6
                )} ${pair?.borrowToken.tokenSymbol}`
              : "-"}
          </PositionValueSmall>
        </ValuesContainer>
      </PositionDetail>
      <PositionDetail>
        <PositionHeaderM>Fee APR</PositionHeaderM>
        <PositionValue>{feeApy ? `${feeApy} %` : "-"}</PositionValue>
      </PositionDetail>
      <PositionDetail>
        <PositionHeaderM>Leverage</PositionHeaderM>
        <PositionValue>
          {borrowRatio ? `${+borrowRatio / 10000 + 1}X` : "-"}
        </PositionValue>
      </PositionDetail>
      <PositionDetail>
        <PositionHeaderM>Start Time</PositionHeaderM>
        <div>
          <TimeValue>
            {new Date(openTime * 1000).toLocaleDateString("en-US")}
          </TimeValue>
          <TimeValue>
            {new Date(openTime * 1000).toLocaleTimeString("en-US")}
          </TimeValue>
        </div>
      </PositionDetail>
      <PositionDetail>
        <PositionHeaderM>Link</PositionHeaderM>
        <LinkIcon
          icon={faArrowUpRightFromSquare}
          onClick={() => openUniPage(pos.PositionId)}
        />
      </PositionDetail>
    </PositionRow>
  );
};

export default ActivePosition;

const PositionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr repeat(5, 1fr) 60px;
  align-items: center;
  background-color: ${Colors.backgroundGray4};
  border-radius: 8px;
  padding: 20px;
  margin-top: 12px;
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
  }
  div:not(:first-child) {
    justify-content: flex-end;
    text-align: right;
    @media (max-width: 768px) {
      justify-content: space-between;
    }
  }
  h4:not(:first-child) {
    text-align: right;
  }
`;

const PositionHeader = styled(H4)`
  color: ${Colors.gray4};
  font-size: 14px;
  line-height: 22px;
  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 22px;
  }
  @media (max-width: 576px) {
    font-size: 12px;
    line-height: 20px;
  }
`;

const PositionValue = styled(H4)`
  color: ${Colors.gray1};
  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 22px;
  }
  @media (max-width: 576px) {
    font-size: 12px;
    line-height: 20px;
  }
`;

const PositionValueSmall = styled(H4)`
  color: ${Colors.gray1};
  font-size: 14px;
  line-height: 22px;
  @media (max-width: 576px) {
    font-size: 12px;
    line-height: 20px;
  }
`;

const PositionDetail = styled.div`
  display: flex;
  justify-content: space-between;
  @media (max-width: 768px) {
    width: 100%;
    padding: 4px 0;
  }
`;

const PositionHeaderM = styled(PositionHeader)`
  display: none;
  @media (max-width: 768px) {
    display: block;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
`;

const ValuesContainer = styled.div`
  @media (max-width: 768px) {
    text-align: right;
  }
`;

const TimeValue = styled(H4)`
  color: ${Colors.gray1};
  font-size: 14px;
  line-height: 22px;
  @media (max-width: 576px) {
    font-size: 12px;
    line-height: 20px;
  }
`;

const LinkIcon = styled(FontAwesomeIcon)`
  color: ${Colors.gray1};
  font-size: 14px;
  padding: 4px;
  :hover {
    cursor: pointer;
  }
  :active {
    transform: scale(0.95);
  }
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;
